import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createRouter, RouterProvider } from '@tanstack/react-router'
import { Fragment, StrictMode, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { aplicarIdioma, idiomaPreferido, useIdioma } from './lib/i18n'
import { applyTheme, preferredTheme } from './lib/theme'
import { createClient, TRPCProvider } from './lib/trpc'
import { routeTree } from './routeTree.gen'
import './styles.css'

// Antes do primeiro render, e fora do layout autenticado: login e convite
// também respeitam o tema do aparelho, e ninguém vê um flash claro antes.
applyTheme(preferredTheme())

// Mesma razão, para o idioma: login, convite e a tela de carregamento vêm
// antes da sessão, e sem isto um chileno veria português até o `me` chegar.
// A escolha gravada no banco entra depois, na casca autenticada (`_app.tsx`).
aplicarIdioma(idiomaPreferido())

const router = createRouter({ routeTree, defaultPreload: 'intent' })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

function App() {
  // Criados no estado para não vazarem entre remontagens em dev (HMR).
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // O realtime chega pelo WebSocket do WorkspaceHub e invalida as
            // queries; refetch por foco em cima disso só gera request à toa.
            refetchOnWindowFocus: false,
            staleTime: 30_000,
            retry: 1,
          },
        },
      }),
  )
  const [trpcClient] = useState(() => createClient())
  const idioma = useIdioma()

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {/**
         * A chave por idioma remonta a árvore inteira quando a escolha muda.
         *
         * É de propósito, e é o que torna seguro `t()` ser função de módulo em
         * vez de hook: sem a remontagem, qualquer componente que não assinasse
         * a troca ficaria com o texto do idioma anterior — e num app deste
         * tamanho esse "qualquer" é a maioria. Trocar de idioma é gesto raro e
         * deliberado; perder o painel aberto e a rolagem uma vez custa menos do
         * que meia tela em português no meio do espanhol.
         *
         * Fica dentro dos providers: o cache de query e a sessão sobrevivem,
         * então nada é buscado de novo na rede.
         */}
        <Fragment key={idioma}>
          <RouterProvider router={router} />
        </Fragment>
      </TRPCProvider>
    </QueryClientProvider>
  )
}

/**
 * Altura real da janela no PWA do iOS.
 *
 * Em web app instalado o iOS entrega um viewport de layout mais curto que a
 * tela — e já assim na carga fria, sem teclado nenhum (medido em prints:
 * janela de 812 numa tela de 874, e o escurecido da gaveta, que é `fixed
 * inset-0`, parando junto). A janela **é** pintada inteira, porque o fundo do
 * `body` propaga para o canvas; o que para cedo é o layout. Resultado: casca,
 * composer e gaveta terminavam antes da borda e sobrava uma faixa da cor do
 * `paper` no rodapé.
 *
 * A correção é medida, não escrita: só entra quando a janela é menor que a
 * tela **em no máximo o recorte do topo**, que é exatamente o que o iOS
 * desconta. Uma janela menor por outro motivo (PWA em tela dividida no iPad)
 * sobra dessa faixa e fica como está. Onde não há diferença — todo aparelho
 * são, e o navegador comum — a propriedade nem chega a ser definida e o CSS
 * fica no `100%` de sempre.
 *
 * Só no app instalado: no Safari a diferença entre tela e janela é a barra do
 * navegador, que não é bug nenhum e não se corrige.
 *
 * **O encolhimento não chega na mesma hora toda vez.** Em algumas aberturas
 * ele já vale na primeira medida; em outras aparece depois, e uma medida
 * colhida antes disso conclui "está tudo certo". Duas tentativas de acertar o
 * instante falharam: a faixa sumia no primeiro abrir e voltava no terceiro ou
 * quarto. Então o instante saiu da conta — o aparelho mede uma vez e o app
 * guarda o resultado, chaveado pela tela em que foi medido. Da próxima
 * abertura a correção já entra antes do primeiro quadro, sem depender de
 * medida nenhuma.
 *
 * A decisão só liga, nunca desliga: dentro da sessão, uma medida ruim não
 * desfaz o que já valia. O custo assumido é que, se um dia o iOS consertar o
 * bug, o aparelho que já mediu continua corrigindo até reinstalar o app —
 * barato perto de uma faixa que volta sozinha. Se isso virar problema, o lugar
 * de resolver é aqui.
 */
const MEMORIA_FOLGA = 'reydelgreen:janela-curta'

let corrigir = false

function ligarCorrecao() {
  corrigir = true
  document.documentElement.style.setProperty('--altura-janela', `${screen.height}px`)
}

function medirJanela() {
  if (corrigir) return

  const raiz = document.documentElement
  // `env()` não se lê daqui; `--sat` é publicado em `styles.css` para isto.
  const recorte = Number.parseFloat(getComputedStyle(raiz).getPropertyValue('--sat')) || 0
  // `clientHeight` da raiz é a altura do viewport de layout — exatamente o que
  // um `height: 100%` resolve. É a grandeza certa a comparar, e não a janela
  // que o sistema reporta.
  const diferenca = screen.height - raiz.clientHeight
  if (diferenca <= 0 || diferenca > recorte) return

  ligarCorrecao()
  localStorage.setItem(MEMORIA_FOLGA, String(screen.height))
}

const instalado =
  (navigator as { standalone?: boolean }).standalone === true ||
  window.matchMedia('(display-mode: standalone)').matches

if (instalado) {
  // Antes de medir qualquer coisa: o que este aparelho já mostrou na primeira
  // vez. Chaveado pela tela, para um aparelho diferente (ou rotacionado) não
  // herdar conta que não é dele.
  if (localStorage.getItem(MEMORIA_FOLGA) === String(screen.height)) ligarCorrecao()

  medirJanela()
  // No primeiro quadro a conta já enxerga `--sat` resolvido; as reconferências
  // seguintes existem para as aberturas em que o encolhimento chega atrasado.
  requestAnimationFrame(medirJanela)
  for (const atraso of [150, 500, 1500, 3000]) setTimeout(medirJanela, atraso)

  window.addEventListener('resize', medirJanela)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    medirJanela()
    for (const atraso of [150, 500, 1500]) setTimeout(medirJanela, atraso)
  })
}

const container = document.getElementById('root')
if (!container) throw new Error('#root não encontrado')

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

/**
 * Service worker: casca em cache, dados sempre da rede.
 *
 * Registrado depois do `load` para não disputar banda com o primeiro render,
 * e só em produção — em desenvolvimento ele serviria o bundle antigo e
 * esconderia mudanças recém-salvas.
 */
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch((erro) => {
      console.warn('service worker não registrou', erro)
    })
  })

  /**
   * Versão nova assumiu o controle: recarrega.
   *
   * No PWA do iPhone o app é retomado do estado em que ficou, não recarregado
   * — uma correção publicada podia ficar dias sem aparecer, e do lado de cá
   * parecia que o deploy não tinha saído. O `skipWaiting` do worker faz a
   * troca acontecer sozinha; aqui a página acompanha.
   *
   * A trava evita laço: sem ela, um worker que reassume no meio do
   * carregamento recarregaria de novo, e de novo.
   */
  let recarregando = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (recarregando) return
    recarregando = true
    window.location.reload()
  })

  /**
   * Procura versão nova toda vez que o app volta para a frente.
   *
   * No iPhone o PWA é retomado do ponto em que ficou — pode passar dias sem
   * uma navegação de verdade, e sem navegação o worker nunca é rebuscado. É o
   * que fazia uma correção publicada não aparecer: o deploy tinha saído, o
   * aparelho é que continuava com o pacote velho.
   */
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return
    navigator.serviceWorker.getRegistration().then((registro) => registro?.update())
  })
}
